import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from '../../utils/axios';
import { getImageUrl } from '../../utils/helpers';
import useActivityTypes from '../../hooks/useActivityTypes';
import ImageGalleryManager from '../../components/admin/ImageGalleryManager';
import {
  DEFAULT_CANCELLATION_EMAIL_HTML,
  DEFAULT_CONFIRMATION_EMAIL_HTML,
  DEFAULT_PREPARATION_HTML,
} from '../../utils/defaultEmailTemplates';

const emptyForm = {
  name: '',
  displayName: '',
  color: '#808080',
  image: '',
  imageUrl: '',
  showOnHome: false,
  isActive: true,
  order: 0,
  shortDescription: '',
  longDescription: '',
  defaultDuration: 60,
  defaultMaxParticipants: 10,
  defaultTitle: '',
  bookingMode: 'group',
  deductsSubscription: true,
  maxParticipantsPerBooking: 4,
  pairsWithIndividualSlots: false,
  treatmentTypesText: '',
  confirmationEmailHtml: DEFAULT_CONFIRMATION_EMAIL_HTML,
  cancellationEmailHtml: DEFAULT_CANCELLATION_EMAIL_HTML,
  extraPreparationHtml: DEFAULT_PREPARATION_HTML,
};

const BOOKING_MODES = [
  { value: 'group', label: 'Cours collectif' },
  { value: 'individual', label: 'Séance individuelle' },
  { value: 'individual_with_treatment', label: 'Individuel avec type de soin' },
];

const ActivityTypeManagement = () => {
  const { t } = useTranslation();
  const { types, loading, error, refresh } = useActivityTypes({ admin: true });
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [pageTab, setPageTab] = useState(0);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null, loading: false, error: '' });

  const rows = useMemo(
    () =>
      types.map((type) => ({
        id: type._id,
        ...type,
      })),
    [types]
  );

  const openCreate = () => {
    setSelected(null);
    setForm({ ...emptyForm });
    setFormError('');
    setTab(0);
    setOpen(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setForm({
      name: row.name || '',
      displayName: row.displayName || '',
      color: row.color || '#808080',
      image: row.image || '',
      imageUrl: row.image || '',
      showOnHome: !!row.showOnHome,
      isActive: row.isActive !== false,
      order: row.order ?? 0,
      shortDescription: row.shortDescription || '',
      longDescription: row.longDescription || '',
      defaultDuration: row.defaultDuration ?? 60,
      defaultMaxParticipants: row.defaultMaxParticipants ?? 10,
      defaultTitle: row.defaultTitle || row.name || '',
      bookingMode: row.bookingMode || 'group',
      deductsSubscription: row.deductsSubscription !== false,
      maxParticipantsPerBooking: row.maxParticipantsPerBooking ?? 4,
      pairsWithIndividualSlots: !!row.pairsWithIndividualSlots,
      treatmentTypesText: (row.treatmentTypes || []).join(', '),
      confirmationEmailHtml: row.confirmationEmailHtml || DEFAULT_CONFIRMATION_EMAIL_HTML,
      cancellationEmailHtml: row.cancellationEmailHtml || DEFAULT_CANCELLATION_EMAIL_HTML,
      extraPreparationHtml: row.extraPreparationHtml || DEFAULT_PREPARATION_HTML,
    });
    setFormError('');
    setTab(0);
    setOpen(true);
  };

  const handleChange = (field) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'bookingMode') {
        if (value === 'individual' || value === 'individual_with_treatment') {
          next.maxParticipantsPerBooking = 1;
          next.defaultMaxParticipants = 1;
          next.deductsSubscription = false;
          next.pairsWithIndividualSlots = true;
        } else {
          next.maxParticipantsPerBooking = 4;
          next.deductsSubscription = true;
          next.pairsWithIndividualSlots = false;
        }
      }
      if (field === 'name' && !selected && (!prev.defaultTitle || prev.defaultTitle === prev.name)) {
        next.defaultTitle = value;
      }
      if (field === 'name' && !selected && (!prev.displayName || prev.displayName === prev.name)) {
        next.displayName = value;
      }
      return next;
    });
  };

  const buildPayload = () => {
    const treatmentTypes = form.treatmentTypesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    return {
      name: form.name.trim(),
      displayName: (form.displayName || form.name).trim(),
      color: form.color,
      imageUrl: form.imageUrl || form.image || '',
      showOnHome: form.showOnHome,
      isActive: form.isActive,
      order: Number(form.order) || 0,
      shortDescription: form.shortDescription,
      longDescription: form.longDescription,
      defaultDuration: Number(form.defaultDuration) || 60,
      defaultMaxParticipants: Number(form.defaultMaxParticipants) || 10,
      defaultTitle: (form.defaultTitle || form.name || '').trim(),
      bookingMode: form.bookingMode,
      deductsSubscription: form.deductsSubscription,
      maxParticipantsPerBooking: Number(form.maxParticipantsPerBooking) || 4,
      pairsWithIndividualSlots: form.pairsWithIndividualSlots,
      treatmentTypes: JSON.stringify(treatmentTypes),
      confirmationEmailHtml: form.confirmationEmailHtml || DEFAULT_CONFIRMATION_EMAIL_HTML,
      cancellationEmailHtml: form.cancellationEmailHtml || DEFAULT_CANCELLATION_EMAIL_HTML,
      extraPreparationHtml: form.extraPreparationHtml || DEFAULT_PREPARATION_HTML,
    };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFormError('');
      if (!form.name.trim()) {
        setFormError('Le nom interne est obligatoire');
        setTab(0);
        return;
      }
      if (form.bookingMode === 'individual_with_treatment' && !form.treatmentTypesText.trim()) {
        setFormError('Indiquez au moins un type de soin (séparés par des virgules)');
        setTab(4);
        return;
      }

      const payload = buildPayload();
      const body = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) body.append(key, value);
      });

      if (selected) {
        await axios.put(`/api/activity-types/${selected._id}`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post('/api/activity-types', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setOpen(false);
      await refresh();
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row, isActive) => {
    try {
      await axios.patch(`/api/activity-types/${row._id}/active`, { isActive });
      await refresh();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Impossible de changer le statut');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteDialog({ open: true, row, loading: false, error: '' });
  };

  const closeDeleteDialog = () => {
    if (deleteDialog.loading) return;
    setDeleteDialog({ open: false, row: null, loading: false, error: '' });
  };

  const confirmDelete = async () => {
    const row = deleteDialog.row;
    if (!row) return;
    try {
      setDeleteDialog((prev) => ({ ...prev, loading: true, error: '' }));
      await axios.delete(`/api/activity-types/${row._id}`);
      setDeleteDialog({ open: false, row: null, loading: false, error: '' });
      await refresh();
    } catch (err) {
      setDeleteDialog((prev) => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || 'Erreur lors de la suppression',
      }));
    }
  };

  const handleImageSelect = (image) => {
    const url = image.url || image.path || '';
    setForm((prev) => ({
      ...prev,
      imageUrl: url,
      image: url,
    }));
  };

  const columns = [
    {
      field: 'color',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: params.value || '#808080',
            border: '1px solid rgba(0,0,0,0.2)',
          }}
        />
      ),
    },
    { field: 'name', headerName: 'Nom interne', flex: 1, minWidth: 140 },
    { field: 'displayName', headerName: 'Libellé', flex: 1, minWidth: 140 },
    {
      field: 'bookingMode',
      headerName: 'Mode',
      width: 160,
      valueGetter: (params) =>
        BOOKING_MODES.find((m) => m.value === params.value)?.label || params.value,
    },
    {
      field: 'showOnHome',
      headerName: 'Home',
      width: 80,
      renderCell: (params) => (params.value ? 'Oui' : 'Non'),
    },
    {
      field: 'isActive',
      headerName: 'Actif',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Switch
          size="small"
          checked={params.value !== false}
          onChange={(e) => handleToggleActive(params.row, e.target.checked)}
          color="success"
          inputProps={{ 'aria-label': 'Activer / désactiver' }}
        />
      ),
    },
    { field: 'order', headerName: 'Ordre', width: 70 },
    { field: 'usageCount', headerName: 'Séances', width: 90 },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('edit')}>
            <IconButton size="small" color="primary" onClick={() => openEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('delete')}>
            <IconButton size="small" color="error" onClick={() => openDeleteDialog(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Types d&apos;activités</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>
            Rafraîchir
          </Button>
          {pageTab === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Nouveau type
            </Button>
          )}
        </Stack>
      </Stack>

      <Tabs
        value={pageTab}
        onChange={(_, v) => setPageTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Types" />
        <Tab label="Images" />
      </Tabs>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Impossible de charger les types ({error.response?.status || error.message}).
        </Alert>
      )}

      {pageTab === 1 && (
        <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Galerie partagée avec Gestion des activités — importez ou supprimez sans créer de type.
          </Typography>
          <ImageGalleryManager
            selectable={false}
            helperText="Importez une photo JPG/PNG, ou supprimez une vignette via la corbeille."
          />
        </Box>
      )}

      {pageTab === 0 && (
        <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Ces types pilotent la home, les couleurs d&apos;agenda, les durées/capacités/titre par défaut,
        les règles d&apos;inscription et les emails. La poubelle annule les séances futures inscrites
        (emails d&apos;annulation) puis désactive ou supprime le type.
      </Alert>

      <Box sx={{ height: 560, width: '100%', bgcolor: 'background.paper' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
        />
      </Box>
        </>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selected ? `Modifier — ${selected.name}` : 'Nouveau type d\'activité'}
            </Typography>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
            <Tab label="Contenu" />
            <Tab label="Visuel" />
            <Tab label="Images" />
            <Tab label="Defaults" />
            <Tab label="Réservation" />
            <Tab label="Emails" />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={2}>
              <TextField
                label="Nom interne (filtres / séances)"
                value={form.name}
                onChange={handleChange('name')}
                required
                fullWidth
                helperText="Ex: Hatha Yoga — utilisé pour lier les séances"
                disabled={!!selected}
              />
              <TextField
                label="Libellé affiché"
                value={form.displayName}
                onChange={handleChange('displayName')}
                fullWidth
                helperText="Ex: Massage Intuitif pour le type Soin"
              />
              <TextField
                label="Description courte"
                value={form.shortDescription}
                onChange={handleChange('shortDescription')}
                fullWidth
                multiline
                minRows={2}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Description longue</Typography>
                <ReactQuill
                  theme="snow"
                  value={form.longDescription}
                  onChange={(value) => setForm((prev) => ({ ...prev, longDescription: value }))}
                />
              </Box>
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Couleur agenda"
                  type="color"
                  value={form.color}
                  onChange={handleChange('color')}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="Hex"
                  value={form.color}
                  onChange={handleChange('color')}
                  sx={{ width: 140 }}
                />
                <Chip
                  label={form.displayName || form.name || 'Aperçu'}
                  sx={{ bgcolor: form.color, color: '#fff' }}
                />
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Image sélectionnée
                </Typography>
                {form.imageUrl ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                    <Box
                      component="img"
                      src={getImageUrl(form.imageUrl)}
                      alt=""
                      sx={{
                        width: 260,
                        height: 160,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid #eee',
                      }}
                    />
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {form.imageUrl}
                      </Typography>
                      <Button size="small" onClick={() => setTab(2)}>
                        Changer dans Images
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => setForm((prev) => ({ ...prev, imageUrl: '', image: '' }))}
                      >
                        Retirer l&apos;image
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Alert
                    severity="info"
                    action={
                      <Button color="inherit" size="small" onClick={() => setTab(2)}>
                        Ouvrir Images
                      </Button>
                    }
                  >
                    Aucune image — importez ou choisissez-en une dans l&apos;onglet Images.
                  </Alert>
                )}
              </Box>

              <FormControlLabel
                control={<Checkbox checked={form.showOnHome} onChange={handleChange('showOnHome')} />}
                label="Afficher sur la page d'accueil"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    color="success"
                  />
                }
                label={form.isActive ? 'Actif' : 'Non actif'}
              />
              <TextField
                label="Ordre d'affichage"
                type="number"
                value={form.order}
                onChange={handleChange('order')}
                sx={{ width: 160 }}
              />
            </Stack>
          )}

          {tab === 2 && (
            <ImageGalleryManager
              selectedUrl={form.imageUrl}
              onSelect={handleImageSelect}
              onDeleted={(image) => {
                if (form.imageUrl === image.url) {
                  setForm((prev) => ({ ...prev, imageUrl: '', image: '' }));
                }
              }}
            />
          )}

          {tab === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Durée par défaut (min)"
                  type="number"
                  fullWidth
                  value={form.defaultDuration}
                  onChange={handleChange('defaultDuration')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Participants max par défaut"
                  type="number"
                  fullWidth
                  value={form.defaultMaxParticipants}
                  onChange={handleChange('defaultMaxParticipants')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Titre par défaut"
                  fullWidth
                  value={form.defaultTitle}
                  onChange={handleChange('defaultTitle')}
                  helperText="Préremplit le titre de la séance dans Gestion des activités"
                />
              </Grid>
            </Grid>
          )}

          {tab === 4 && (
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Mode de réservation</InputLabel>
                <Select
                  label="Mode de réservation"
                  value={form.bookingMode}
                  onChange={handleChange('bookingMode')}
                >
                  {BOOKING_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Checkbox checked={form.deductsSubscription} onChange={handleChange('deductsSubscription')} />}
                label="Prélève sur l'abonnement (crédits restants)"
              />
              <FormControlLabel
                control={<Checkbox checked={form.pairsWithIndividualSlots} onChange={handleChange('pairsWithIndividualSlots')} />}
                label="Bloque les autres créneaux individuels au même horaire"
              />
              <TextField
                label="Max participants par inscription"
                type="number"
                value={form.maxParticipantsPerBooking}
                onChange={handleChange('maxParticipantsPerBooking')}
                inputProps={{ min: 1, max: 4 }}
                sx={{ width: 280 }}
              />
              {form.bookingMode === 'individual_with_treatment' && (
                <TextField
                  label="Types de soin (virgules)"
                  value={form.treatmentTypesText}
                  onChange={handleChange('treatmentTypesText')}
                  fullWidth
                  helperText="Ex: Shiatsu, CNT, Massage à l'huile"
                />
              )}
            </Stack>
          )}

          {tab === 5 && (
            <Stack spacing={3}>
              <Alert severity="info">
                Placeholders : {'{{userName}}'}, {'{{activityTitle}}'}, {'{{categoryName}}'}, {'{{date}}'}, {'{{time}}'}, {'{{duration}}'}, {'{{location}}'}, {'{{treatmentType}}'}, {'{{remainingActivities}}'}, {'{{numberOfParticipants}}'}.
                Les champs sont préremplis — vous pouvez les adapter.
              </Alert>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Email de confirmation</Typography>
                <ReactQuill
                  theme="snow"
                  value={form.confirmationEmailHtml}
                  onChange={(value) => setForm((prev) => ({ ...prev, confirmationEmailHtml: value }))}
                />
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Email d&apos;annulation</Typography>
                <ReactQuill
                  theme="snow"
                  value={form.cancellationEmailHtml}
                  onChange={(value) => setForm((prev) => ({ ...prev, cancellationEmailHtml: value }))}
                />
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Bloc préparation / consignes</Typography>
                <ReactQuill
                  theme="snow"
                  value={form.extraPreparationHtml}
                  onChange={(value) => setForm((prev) => ({ ...prev, extraPreparationHtml: value }))}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : t('save') || 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Supprimer le type d&apos;activité</Typography>
            <IconButton onClick={closeDeleteDialog} size="small" disabled={deleteDialog.loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteDialog.error}</Alert>
          )}
          <DialogContentText component="div">
            <Typography paragraph>
              Vous allez supprimer le type « <strong>{deleteDialog.row?.name}</strong> ».
            </Typography>
            <Typography paragraph>
              Les séances <strong>futures</strong> (après maintenant) de ce type seront annulées.
              Les participants inscrits recevront un email d&apos;annulation et leurs crédits seront
              restaurés si applicable.
            </Typography>
            <Typography paragraph>
              Les séances passées sont conservées pour l&apos;historique ; le type sera alors
              désactivé plutôt que supprimé définitivement.
            </Typography>
            {(deleteDialog.row?.usageCount || 0) > 0 && (
              <Alert severity="warning">
                {deleteDialog.row.usageCount} séance(s) référencent actuellement ce type.
              </Alert>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteDialog.loading}>
            {t('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteDialog.loading}
          >
            {deleteDialog.loading ? 'Suppression…' : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityTypeManagement;
